import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ExternalLink, SkipForward, Loader2, AlertCircle, PartyPopper } from 'lucide-react';

// ─── Public types ────────────────────────────────────────────────────────────

export type StepStatus = 'pending' | 'done' | 'skipped';

export interface StepState {
  status: StepStatus;
  variant?: string;
}

/**
 * A selectable path inside a step.
 * When a step has variants, the user picks which applies to them
 * before seeing the instructions.
 */
export interface TutorialVariant {
  key: string;
  label: string;
  body: React.ReactNode;
}

/**
 * A single step in the tutorial.
 */
export interface TutorialStep {
  id: string;
  title: string;

  // Simple content — use when there are no variants
  body?: React.ReactNode;

  // Conditional content — user picks which variant applies to their case
  variants?: TutorialVariant[];
  variantPrompt?: string; // e.g. "¿Cómo es tu situación?"

  // Optional CTA button inside the step
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };

  // Label for the "I did it" toggle. Default: "Ya lo hice"
  doneLabel?: string;

  // When true, shows a secondary "Not applicable" option
  skippable?: boolean;
  skipLabel?: string; // Default: "No aplica para mí"

  // Optional async validation — called when user clicks "Verificar".
  // Resolve = auto-mark done. Reject = show the error message.
  validate?: () => Promise<void>;
  validateLabel?: string; // Default: "Verificar"
}

export interface TutorialChecklistProps {
  title: string;
  subtitle?: string;
  steps: TutorialStep[];

  // Completion
  completionTitle?: string;
  completionBody?: React.ReactNode;
  onComplete?: () => void;

  // Persistence — lift state up to save/restore between sessions
  initialState?: Record<string, StepState>;
  onStateChange?: (state: Record<string, StepState>) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TutorialChecklist({
  title,
  subtitle,
  steps,
  completionTitle = '¡Todo listo!',
  completionBody,
  onComplete,
  initialState = {},
  onStateChange,
}: TutorialChecklistProps) {
  const [state, setState] = useState<Record<string, StepState>>(() => {
    const s: Record<string, StepState> = {};
    steps.forEach((step) => {
      s[step.id] = initialState[step.id] ?? { status: 'pending' };
    });
    return s;
  });

  // Which step header is expanded
  const [expanded, setExpanded] = useState<string | null>(() => {
    const firstPending = steps.find(
      (s) => !(initialState[s.id] && initialState[s.id].status !== 'pending'),
    );
    return firstPending?.id ?? null;
  });

  // Per-step validation state
  const [validating, setValidating] = useState<Record<string, boolean>>({});
  const [validateError, setValidateError] = useState<Record<string, string>>({});

  const doneCount = useMemo(
    () => steps.filter((s) => state[s.id]?.status !== 'pending').length,
    [state, steps],
  );

  const allDone = doneCount === steps.length;

  // ── State helpers ──────────────────────────────────────────────────────────

  const updateState = useCallback(
    (id: string, patch: Partial<StepState>) => {
      setState((prev) => {
        const next = { ...prev, [id]: { ...prev[id], ...patch } };
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange],
  );

  function advanceToNextPending(fromId: string) {
    const idx = steps.findIndex((s) => s.id === fromId);
    const next = steps.slice(idx + 1).find((s) => state[s.id]?.status === 'pending');
    setExpanded(next?.id ?? null);
  }

  function toggleDone(id: string) {
    const current = state[id]?.status;
    if (current === 'done') {
      updateState(id, { status: 'pending' });
      setExpanded(id);
    } else {
      setValidateError((e) => ({ ...e, [id]: '' }));
      updateState(id, { status: 'done' });
      advanceToNextPending(id);

      // Fire onComplete if this was the last pending step
      const remaining = steps.filter(
        (s) => s.id !== id && state[s.id]?.status === 'pending',
      );
      if (remaining.length === 0) onComplete?.();
    }
  }

  function markSkipped(id: string) {
    updateState(id, { status: 'skipped' });
    advanceToNextPending(id);
  }

  function setVariant(id: string, variantKey: string) {
    updateState(id, { variant: variantKey });
    setValidateError((e) => ({ ...e, [id]: '' }));
  }

  async function runValidation(step: TutorialStep) {
    if (!step.validate) return;
    setValidating((v) => ({ ...v, [step.id]: true }));
    setValidateError((e) => ({ ...e, [step.id]: '' }));
    try {
      await step.validate();
      toggleDone(step.id);
    } catch (err) {
      setValidateError((e) => ({
        ...e,
        [step.id]: err instanceof Error ? err.message : 'No se pudo verificar. Inténtalo de nuevo.',
      }));
    } finally {
      setValidating((v) => ({ ...v, [step.id]: false }));
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="bg-ui-surface border border-ui-border rounded-xl overflow-hidden">
      {/* ── Header ── */}
      <div className="px-5 pt-5 pb-4 border-b border-ui-border">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-ui-text">{title}</h3>
            {subtitle && (
              <p className="mt-0.5 text-sm text-ui-text-subtle">{subtitle}</p>
            )}
          </div>
          <span className="shrink-0 tabular-nums text-xs text-ui-text-subtle pt-0.5">
            {doneCount} / {steps.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-ui-bg rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-ui-accent rounded-full origin-left"
            animate={{ scaleX: doneCount / steps.length }}
            style={{ transformOrigin: 'left' }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* ── Completion banner ── */}
      <AnimatePresence>
        {allDone && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="flex gap-3 px-5 py-4 bg-green-500/10 border-b border-green-500/20">
              <PartyPopper size={16} className="shrink-0 mt-0.5 text-green-400" />
              <div>
                <p className="text-sm font-medium text-green-400">{completionTitle}</p>
                {completionBody && (
                  <div className="mt-1 text-sm text-ui-text-subtle">{completionBody}</div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Steps ── */}
      <div className="divide-y divide-ui-border">
        {steps.map((step, idx) => {
          const stepState = state[step.id];
          const isDone = stepState?.status === 'done';
          const isSkipped = stepState?.status === 'skipped';
          const isCompleted = isDone || isSkipped;
          const isOpen = expanded === step.id;
          const activeVariant = step.variants?.find((v) => v.key === stepState?.variant);
          const hasContent = step.body ?? (step.variants && activeVariant);
          const isValidating = validating[step.id];
          const error = validateError[step.id];

          // A step with variants requires picking one before showing the action/done controls
          const readyToConfirm = !step.variants || !!activeVariant;

          return (
            <div key={step.id}>
              {/* ── Step header ── */}
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : step.id)}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-ui-surface-2 transition-colors group"
              >
                {/* Status bubble */}
                <span
                  className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center border transition-colors text-[10px] font-semibold ${
                    isDone
                      ? 'bg-ui-accent border-ui-accent text-ui-text-inverse'
                      : isSkipped
                        ? 'border-ui-border-strong bg-ui-bg text-ui-text-subtle'
                        : 'border-ui-border bg-ui-bg text-ui-text-subtle'
                  }`}
                >
                  {isDone ? (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                    >
                      <Check size={10} strokeWidth={3} />
                    </motion.span>
                  ) : (
                    idx + 1
                  )}
                </span>

                {/* Title */}
                <span className="flex-1 flex items-center gap-2 min-w-0">
                  <span
                    className={`text-sm font-medium truncate ${
                      isCompleted ? 'text-ui-text-muted line-through decoration-ui-border-strong' : 'text-ui-text'
                    }`}
                  >
                    {step.title}
                  </span>
                  {isSkipped && (
                    <span className="shrink-0 text-xs text-ui-text-subtle font-normal" style={{ textDecoration: 'none' }}>
                      omitido
                    </span>
                  )}
                </span>

                {/* Chevron */}
                <motion.span
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.18 }}
                  className="shrink-0 text-ui-text-subtle group-hover:text-ui-text-muted"
                >
                  <ChevronDown size={14} />
                </motion.span>
              </button>

              {/* ── Step body ── */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="body"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 pt-1 pl-[3.25rem]">

                      {/* ── Variant picker ── */}
                      {step.variants && (
                        <div className="mb-4">
                          {step.variantPrompt && (
                            <p className="text-xs text-ui-text-subtle mb-2 font-medium">
                              {step.variantPrompt}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {step.variants.map((v) => (
                              <button
                                key={v.key}
                                type="button"
                                onClick={() => setVariant(step.id, v.key)}
                                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                  stepState?.variant === v.key
                                    ? 'bg-ui-accent border-ui-accent text-ui-text-inverse font-medium'
                                    : 'border-ui-border text-ui-text-muted hover:border-ui-accent/50 hover:text-ui-text'
                                }`}
                              >
                                {v.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── Content ── */}
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={stepState?.variant ?? 'default'}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.15 }}
                        >
                          {step.variants && !activeVariant ? (
                            <p className="text-sm text-ui-text-subtle italic">
                              Selecciona tu caso arriba para ver las instrucciones.
                            </p>
                          ) : (
                            <div className="text-sm text-ui-text-muted leading-relaxed space-y-3">
                              {activeVariant ? activeVariant.body : step.body}
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>

                      {/* ── Action link / button ── */}
                      {readyToConfirm && step.action && (
                        <div className="mt-4">
                          {step.action.href ? (
                            <a
                              href={step.action.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-ui-surface-2 border border-ui-border rounded-md text-ui-text hover:border-ui-accent/50 transition-colors"
                            >
                              {step.action.label}
                              <ExternalLink size={11} />
                            </a>
                          ) : (
                            <button
                              type="button"
                              onClick={step.action.onClick}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-ui-surface-2 border border-ui-border rounded-md text-ui-text hover:border-ui-accent/50 transition-colors"
                            >
                              {step.action.label}
                            </button>
                          )}
                        </div>
                      )}

                      {/* ── Validation error ── */}
                      <AnimatePresence>
                        {error && (
                          <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="mt-3 flex items-start gap-2 text-xs text-red-300"
                          >
                            <AlertCircle size={13} className="shrink-0 mt-0.5" />
                            {error}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* ── Done / Skip / Validate controls ── */}
                      {readyToConfirm && (hasContent || !step.variants) && (
                        <div className="mt-4 pt-3 border-t border-ui-border flex items-center gap-3 flex-wrap">
                          {/* Auto-validate button */}
                          {step.validate && !isDone && (
                            <button
                              type="button"
                              onClick={() => runValidation(step)}
                              disabled={isValidating}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse rounded-md disabled:opacity-60 transition-colors"
                            >
                              {isValidating ? (
                                <Loader2 size={11} className="animate-spin" />
                              ) : (
                                <Check size={11} strokeWidth={3} />
                              )}
                              {isValidating ? 'Verificando…' : (step.validateLabel ?? 'Verificar')}
                            </button>
                          )}

                          {/* Manual done toggle */}
                          <button
                            type="button"
                            onClick={() => toggleDone(step.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                              isDone
                                ? 'bg-ui-accent border-ui-accent text-ui-text-inverse'
                                : step.validate
                                  ? 'border-ui-border text-ui-text-subtle hover:text-ui-text-muted'
                                  : 'border-ui-border text-ui-text-muted hover:border-ui-accent/60 hover:text-ui-text'
                            }`}
                          >
                            <Check size={11} strokeWidth={3} />
                            {isDone ? '¡Listo!' : (step.doneLabel ?? 'Ya lo hice')}
                          </button>

                          {/* Skip */}
                          {step.skippable && !isDone && (
                            <button
                              type="button"
                              onClick={() => markSkipped(step.id)}
                              className="inline-flex items-center gap-1.5 text-xs text-ui-text-subtle hover:text-ui-text-muted transition-colors"
                            >
                              <SkipForward size={11} />
                              {step.skipLabel ?? 'No aplica para mí'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
